import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  ShieldCheckIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import ApiLoader from "../../ui/ApiLoader";
import { getAssetsByOwner } from "../../../services/axios/AuthApis/AssetManagementApis";

const AssetsModal = ({ show, onClose, member }) => {
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && member?.employee_id) {
      fetchMemberAssets();
    }
  }, [show, member]);

  const fetchMemberAssets = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the employee_id as owner_id to fetch connectors
      const response = await getAssetsByOwner(member.user_id);

      if (response && response.data) {
        setAssets(response.data);
      } else {
        setAssets([]);
      }
    } catch (err) {
      console.error("Error fetching member assets:", err);
      setError("Failed to load assets");
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const getCriticalityColor = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getConnectorTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "apikey":
        return "bg-cyan-100 text-cyan-800";
      case "oauth2":
        return "bg-purple-100 text-purple-800";
      case "scim":
        return "bg-indigo-100 text-indigo-800";
      case "basicauth":
        return "bg-green-100 text-green-800";
      case "pat":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <CubeIcon className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Assets Owned by {member?.firstname} {member?.lastname}
                </h2>
                {/* <p className="text-sm text-gray-600">
                  Connectors and assets assigned to this team member
                </p> */}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <ApiLoader label="Loading assets..." />
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <ShieldCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{error}</p>
                </div>
              </div>
            ) : assets.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <CubeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    No assets found for this member
                  </p>
                  {/* <p className="text-sm text-gray-400 mt-1">
                    This team member doesn't own any connectors or assets
                  </p> */}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    {assets.length} asset{assets.length !== 1 ? "s" : ""} found
                  </p>
                </div>

                <div className="grid gap-4">
                  {assets.map((asset) => (
                    <motion.div
                      key={asset.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              {asset.connector_name}
                            </h3>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getConnectorTypeColor(asset.connector_type)}`}
                            >
                              {asset.connector_type}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <p className="text-sm text-gray-600 mb-1">
                                Category
                              </p>
                              <p className="text-sm font-medium text-gray-900">
                                {asset.category_name || "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 mb-1">
                                Criticality
                              </p>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getCriticalityColor(asset.criticality)}`}
                              >
                                {asset.criticality || "N/A"}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 mb-1">
                                Customer
                              </p>
                              <p className="text-sm font-medium text-gray-900">
                                {asset.customer_name || "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 mb-1">
                                Connection Status
                              </p>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  asset.connected
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {asset.connected ? "Connected" : "Disconnected"}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>
                                Created:{" "}
                                {new Date(
                                  asset.created_at,
                                ).toLocaleDateString()}
                              </span>
                              {asset.last_synced_at && (
                                <span>
                                  Last synced:{" "}
                                  {new Date(
                                    asset.last_synced_at,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AssetsModal;
